import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import { Layout } from 'fronton-react';
import { memo } from 'common/hocs/memo';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils/create-element-key';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { CardLinkWithGAPromoClick } from '../card';
import { CardMedia, MediaCard, MediaCardContent } from '../media-card';
import { SectionSubTitle } from '../section-subtitle';
import { SwiperSlide, SwiperWithNavigation } from '../swiper';
import { Typography } from '../typography';
import { useOtherDataSelector } from './hooks';
import {
  cardMediaStyle,
  cardTitleStyle,
  cardDescriptionStyle,
  mediaCardContentStyle,
  otherSectionLayout,
  otherSectionLink,
  otherSectionStyle,
  swiperStyle,
  swiperSlideStyle,
} from './other-section-styles';
import { OtherData } from './other-section-types';

type OtherSectionProps = {
  className?: string;
  elementBlock: string;
  landingName: string;
  promotionName: string;
  title?: string;
};

/**
 * Вью секции "Другие услуги"
 */
export const OtherSection = memo(
  ({ className, elementBlock, landingName, promotionName, title }: OtherSectionProps) => {
    const { t } = useTranslation();
    const otherData = useOtherDataSelector(landingName);
    const refs = useRef<HTMLAnchorElement[]>([]);
    const promotions = useMemo(
      () =>
        otherData.map(({ title: cardTitle }: OtherData, index: number) => ({
          name: promotionName,
          creative: cardTitle,
          position: (index + 1).toString(),
        })),
      [promotionName, otherData]
    );

    usePromoViewEvent(refs, elementBlock, promotions);

    /**
     * Обработчик нажатия кнопки "Следующий слайд"
     */
    const handleNextSlideBtnClick = () => {
      analyticsUtils.clickButtonEvent(elementBlock, ClickButtonElementName.SwitchForward, 'Другие услуги');
    };

    /**
     * Обработчик нажатия кнопки "Предыдущий слайд"
     */
    const handlePrevSlideBtnClick = () => {
      analyticsUtils.clickButtonEvent(elementBlock, ClickButtonElementName.SwitchBackward, 'Другие услуги');
    };

    return (
      <section className={cn(otherSectionStyle, className)}>
        <Layout className={otherSectionLayout}>
          <SectionSubTitle margin="m">{title || t('other-section-title', { ns: 'landings-info', defaultValue: 'Другие услуги' })}</SectionSubTitle>
          <SwiperWithNavigation
            className={swiperStyle}
            onSlideNextTransitionEnd={handleNextSlideBtnClick}
            onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
            slidesPerView="auto"
            watchOverflow>
            {otherData.map((info, index) => (
              <SwiperSlide key={createElementKey('other', index)} className={swiperSlideStyle}>
                <CardLinkWithGAPromoClick
                  bannerRefs={refs}
                  elementBlock={elementBlock}
                  className={otherSectionLink}
                  data-testid="other-banner"
                  href={info.url}
                  position={index}
                  promotion={promotions[index]}
                  title={info.title}>
                  <MediaCard>
                    <CardMedia
                      alt={info.imageAlt}
                      className={cardMediaStyle}
                      loading="lazy"
                      src={info.imageSrc}
                      title={info.imageAlt}
                    />
                    <MediaCardContent className={mediaCardContentStyle}>
                      <Typography className={cardTitleStyle} variant="h3">
                        {info.title}
                      </Typography>
                      {info.description && (
                        <Typography className={cardDescriptionStyle} size="body_short" variant="m">
                          {info.description}
                        </Typography>
                      )}
                    </MediaCardContent>
                  </MediaCard>
                </CardLinkWithGAPromoClick>
              </SwiperSlide>
            ))}
          </SwiperWithNavigation>
        </Layout>
      </section>
    );
  }
);
